/**
 * Pricing Button — a wrapper around a real Brandy button.
 *
 * It holds `brandy/buttons` rather than drawing its own anchor, so the button
 * in a pricing card is the same block as every other button on the site: same
 * icon picker, same hover animation, same hover colours, same width control.
 * The earlier version drew its own anchor and had to grow each of those by
 * hand — this one inherits them and cannot fall behind.
 *
 * The wrapper's only job is `data-term`, and only when the button is scoped to
 * one option. A plan carries a single button by default, shown under every
 * option; scoping is for the plan whose options point at different checkouts,
 * and then the author adds a button per option themselves.
 */
import { __, sprintf } from '@wordpress/i18n';
import { registerBlockType } from '@wordpress/blocks';
import {
  useBlockProps,
  useInnerBlocksProps,
  InspectorControls,
  InnerBlocks,
} from '@wordpress/block-editor';
import { PanelBody, SelectControl } from '@wordpress/components';

import metadata from './block.json';
import { ALL_OPTIONS, MAX_PRICING_OPTIONS } from '../_shared/pricing-defaults';

const TEMPLATE = [['brandy/buttons', {}, [['brandy/button', {}]]]];

registerBlockType(metadata.name, {
  /*
   * Named by what it is scoped to. "Button — 2nd option" told an author
   * nothing about which price it sits under, but the option's name lives in
   * the parent's context and a label callback cannot reach it, so the index is
   * spelled out in words instead — the panel inside the block names the
   * actual option.
   */
  __experimentalLabel: (attributes) =>
    attributes?.term >= 0
      ? sprintf(
          /* translators: %d: position of the option the button is scoped to. */
          __('Button — option %d', 'brandy-blocks'),
          attributes.term + 1
        )
      : __('Button', 'brandy-blocks'),

  edit: ({ attributes, setAttributes, context }) => {
    const { term } = attributes;

    const options = (context['brandy/pricingTerms'] || []).slice(
      0,
      MAX_PRICING_OPTIONS
    );

    const blockProps = useBlockProps({
      className: 'brandy-pricing-plan__cta',
      // Absent rather than -1: an unscoped button is not "option minus one",
      // it is a button the switching rules never look at.
      ...(term >= 0 ? { 'data-term': term } : {}),
    });

    const innerBlocksProps = useInnerBlocksProps(blockProps, {
      template: TEMPLATE,
      // The plan locks its children; this reopens the subtree so the button
      // itself stays editable.
      templateLock: false,
    });

    return (
      <>
        {options.length > 1 && (
          <InspectorControls>
            <PanelBody title={__('Button', 'brandy-blocks')}>
              <SelectControl
                label={__('Shown for', 'brandy-blocks')}
                value={String(term ?? ALL_OPTIONS)}
                options={[
                  {
                    label: __('Every option', 'brandy-blocks'),
                    value: String(ALL_OPTIONS),
                  },
                  ...options.map((option, index) => ({
                    label: option.label || String(index + 1),
                    value: String(index),
                  })),
                ]}
                onChange={(value) =>
                  setAttributes({ term: parseInt(value, 10) })
                }
                help={__(
                  'Scope the button to one option only if it needs its own link or wording. Then add a button per option.',
                  'brandy-blocks'
                )}
              />
            </PanelBody>
          </InspectorControls>
        )}

        <div {...innerBlocksProps} />
      </>
    );
  },

  save: () => <InnerBlocks.Content />,
});
